import {
  HardDrive,
  Activity,
  Award,
  BarChart3,
  Briefcase,
  BriefcaseBusiness,
  BookOpen,
  FileText,
  History,
  House,
  Library,
  ListChecks,
  MessageSquareText,
  Palette,
  ShieldCheck,
  UserCog,
  SlidersHorizontal,
  Tags,
  UserCheck,
  UserRound,
  Users,
} from "lucide-react";
import { getLearnerTerms } from "utils/terms";

// `match` lists path prefixes that also highlight the item; `exact` items
// only match their own path.
export const buildNav = ({ role, user, reviewEnabled, reviewCount = 0, isSystemAdmin, commentCount = 0 }) => {
  const learners = getLearnerTerms(user?.club?.instituteId);
  const clubId = user?.club?.id;
  const review = reviewEnabled && {
    key: "review",
    label: "Content safety",
    icon: ShieldCheck,
    to: "/content-review",
    badge: reviewCount,
    description: reviewCount
      ? `${reviewCount} waiting for review`
      : "What screening has held, blocked or removed",
  };

  if (role === "admin") {
    return [
      {
        key: "main",
        items: [
          {
            key: "orgs",
            label: "Home",
            icon: House,
            to: "/",
            exact: true,
            match: ["/clubs", "/club/", "/work-experience-summary"],
            description: "Every organisation, its users, courses and settings",
          },
        ],
      },
      {
        key: "people",
        title: "People",
        items: [
          {
            key: "users",
            label: "All users",
            icon: Users,
            to: "/players",
            description: "Search users across every organisation",
          },
          {
            key: "individual",
            label: "Individual accounts",
            icon: UserRound,
            to: "/individual-accounts",
            description: "Self sign-up accounts outside an organisation",
          },
        ],
      },
      {
        key: "courses",
        title: "Courses",
        items: [
          {
            key: "library",
            label: "Course library",
            icon: Library,
            to: "/courses",
            description: "Qualifications and courses by institution type",
          },
          {
            key: "all-courses",
            label: "All courses",
            icon: BookOpen,
            to: "/all-courses",
            match: ["/course/update"],
            description: "Every course in one list",
          },
          {
            key: "gradings",
            label: "Gradings",
            icon: ListChecks,
            to: "/gradings",
            description: "Grading structures and what uses them",
          },
        ],
      },
      {
        key: "platform",
        title: "Platform",
        items: [
          {
            key: "theme",
            label: "Theme",
            icon: Palette,
            to: "/theme",
            description: "Branding and colours",
          },
          {
            key: "career-lists",
            label: "Career lists",
            icon: ListChecks,
            to: "/career-lists",
            description: "Industries, job roles and answers for career goals",
          },
          {
            key: "activity",
            label: "Activity log",
            icon: History,
            to: "/activity",
            description: "Who did what in the Control Centre",
          },
          {
            key: "storage",
            label: "Storage",
            icon: HardDrive,
            to: "/org-storage",
            description: "Storage limits and use per organisation",
          },
          isSystemAdmin && {
            key: "platform-fields",
            label: "Profile fields",
            icon: SlidersHorizontal,
            to: "/common-user-profile",
            description: "Fields organisations can add to learner profiles",
          },
          isSystemAdmin && {
            key: "system-admins",
            label: "System admins",
            icon: UserCog,
            to: "/system-admins",
            description: "Who can sign in as a Routes admin",
          },
          review,
        ].filter(Boolean),
      },
    ];
  }

  if (role !== "clubAdmin" && role !== "tutor") return [];

  const isClubAdmin = role === "clubAdmin";

  const sections = [
    {
      key: "main",
      items: [{ key: "home", label: "Home", icon: House, to: "/", exact: true }],
    },
    {
      key: "people",
      title: "People",
      items: [
        {
          key: "learners",
          label: learners.plural,
          icon: Users,
          to: "/organisation/users",
          match: [
            "/players/archived",
            "/players/alumni",
            "/players/inactive",
            "/club/archived",
            "/club/alumni",
            "/club/inactive",
          ],
          description: `Add, find and manage ${learners.plural.toLowerCase()}`,
        },
        isClubAdmin && {
          key: "mentors",
          label: "Mentors",
          icon: UserCheck,
          to: "/organisation/mentors",
          match: ["/tutors/archived"],
          description: "Staff who support your " + learners.plural.toLowerCase(),
        },
        {
          key: "comments",
          label: "Comments",
          icon: MessageSquareText,
          to: "/comment-requests",
          badge: commentCount,
          description: commentCount
            ? `${commentCount} waiting for a CV comment`
            : `CV comments ${learners.plural.toLowerCase()} have asked for`,
        },
        isClubAdmin && {
          key: "careers",
          label: "Career goals",
          icon: Briefcase,
          to: "/organisation/careers",
          description: "Career goal questions and employment status",
        },
        isClubAdmin && review,
      ].filter(Boolean),
    },
    {
      key: "learning",
      title: "Learning",
      items: [
        {
          key: "courses",
          label: "Courses",
          icon: BookOpen,
          to: "/organisation/courses",
          match: ["/course/"],
          description: "Courses, units and work experience",
        },
        clubId && {
          key: "reports",
          label: "Reports",
          icon: BarChart3,
          to: `/club/report/${clubId}`,
          match: ["/club/report"],
          description: "Progress and activity across your organisation",
        },
        clubId && {
          key: "work-experience",
          label: "Work experience",
          icon: BriefcaseBusiness,
          to: `/work-experience-summary/${clubId}`,
          match: ["/work-experience-summary"],
          description: "Hours completed against hours required",
        },
      ].filter(Boolean),
    },
  ];

  if (isClubAdmin) {
    sections.push({
      key: "settings",
      title: "Settings",
      items: [
        {
          key: "profile-fields",
          label: "Profile fields",
          icon: SlidersHorizontal,
          to: "/organisation/common-user-profile",
          match: ["/template4-stats"],
          description: `What ${learners.plural.toLowerCase()} fill in on their profile`,
        },
        {
          key: "categories",
          label: "Categories",
          icon: Tags,
          to: "/organisation/player-categories",
          description: `Groups for organising ${learners.plural.toLowerCase()}`,
        },
        {
          key: "cv-sections",
          label: "CV sections",
          icon: FileText,
          to: "/organisation/cv-section-settings",
          description: "Which sections appear on the CV",
        },
        {
          key: "skills",
          label: "Skills",
          icon: Award,
          to: "/organisation/skill-catalogue",
          description: "Your transferable skills catalogue",
        },
        {
          key: "metrics",
          label: "Metrics",
          icon: Activity,
          to: "/organisation/metric-catalogue",
          description: "Performance metrics you track",
        },
      ],
    });
  }

  return sections;
};

const matchesPrefix = (pathname, prefix) =>
  prefix.endsWith("/")
    ? pathname.startsWith(prefix)
    : pathname === prefix || pathname.startsWith(prefix + "/");

export const isNavItemActive = (item, pathname) => {
  if (item.exact ? pathname === item.to : matchesPrefix(pathname, item.to)) {
    return true;
  }
  return (item.match || []).some((prefix) => matchesPrefix(pathname, prefix));
};

export const findActiveNav = (sections, pathname) => {
  for (const section of sections) {
    const item = section.items.find((i) => isNavItemActive(i, pathname));
    if (item) return { section, item };
  }
  return null;
};
